import { SrcType } from "@/models/alt-arts";
import { AaMap, AACard } from "@/models/alt-arts";
import { getCardImgproxyUrl } from "@/lib/imgproxy";
import { cardImageUrl, type CdnGame } from "@/lib/cdn";

export abstract class BaseCardDataManager<T> {
  protected aa: AaMap;
  protected CARD_DATA: Record<string, T> = {};

  constructor(aa: AaMap) {
    this.aa = aa;
  }

  protected getIntFromStr(value: string | null): number {
    let ret = 0;
    if (value !== "-" && value !== null) {
      ret = parseInt(value);
    }
    return ret;
  }

  protected getCorrectedRegion(region: SrcType | ""): string {
    if (region !== "tcgplayer") {
      return region.toLowerCase().replaceAll("_", "-");
    }
    return "";
  }

  protected firstChar(str: string): string {
    return Array.from(str)[0].toLowerCase();
  }

  public getAACardId(aa: AACard): string {
    let srcChar = this.firstChar(aa.src);
    return `${aa.card_number}_${srcChar}${aa.count}`;
  }

  public getAAFromCardId(aaCardId: string): AACard {
    let cardNumber, srcChar, count;
    if (aaCardId.includes("_")) {
      const match = aaCardId.match(/^([A-Za-z0-9-]+)_([A-Za-z])(\d+)$/);
      if (match) {
        cardNumber = match[1]; // captures the card number part before the "_"
        srcChar = match[2]; // captures the single character after the "_"
        count = parseInt(match[3], 10); // captures the count as an integer
      } else {
        throw new Error(`Invalid format for aaCardId: ${aaCardId}`);
      }
    } else {
      // defaults
      cardNumber = aaCardId;
      srcChar = "";
      count = 0;
    }

    return this.getAA(cardNumber, srcChar, count);
  }

  public getAA(cardNumber: string, srcChar: string, count: number): AACard {
    let curSrcChar = srcChar;
    if (srcChar === "p" || srcChar === "") {
      // default or old ids
      const aa = this.getAltArts(cardNumber, true);
      for (const card of aa) {
        // return the first card
        return card;
      }
    }
    const aa = this.getAltArts(cardNumber, true);
    for (const card of aa) {
      if (this.firstChar(card.src) === curSrcChar && card.count === count) {
        return card;
      }
    }
    console.error(
      `card not found: ${cardNumber}, ${curSrcChar}, ${count}, ${JSON.stringify(aa)}`,
    );
    return aa[0];
  }

  public getAltArts(cardId: string, showAAs: boolean): AACard[] {
    if (cardId in this.aa) {
      if (showAAs) {
        return this.aa[cardId].filter((a) => {
          return a.src !== "tcgplayer" && a.hide === false;
        });
      } else {
        return [this.aa[cardId][0]];
      }
    } else {
      console.log({ aa: this.aa });
      console.error(`cardId ${cardId} not found`);
      return [];
    }
  }

  /**
   * Which game folder this manager's images live under, and the cache-busting
   * version for them. Declared per subclass so the shared base is not silently
   * serving one game's art to another.
   */
  protected abstract readonly cdnGame: CdnGame;
  protected readonly imageVersion: string = "1.0";
  /** Region used when the caller supplies none and the card has no alt-art record. */
  protected readonly defaultRegion: string = "en";

  public getCardImage(
    cardId: string,
    size: "original" | "316x440" | "158x220" | "90x125" = "original",
    region: SrcType | "" = "",
  ): string {
    if (!cardId) {
      return "";
    }

    const file_format = "webp";
    const newRegion = this.getCorrectedRegion(region);

    if (newRegion !== "") {
      return getCardImgproxyUrl(
        cardImageUrl(
          this.cdnGame,
          newRegion,
          `${cardId}.${file_format}`,
          this.imageVersion,
        ),
        size,
      );
    }

    // An unknown id has no alt-art record. Fall back to the plain id in the
    // default region rather than dereferencing undefined - callers pass
    // user-supplied ids here.
    const aa = this.getAAFromCardId(cardId);
    const src = this.getCorrectedRegion(aa?.src ?? "") || this.defaultRegion;
    const id = aa?.id ?? cardId;

    return getCardImgproxyUrl(
      cardImageUrl(
        this.cdnGame,
        src,
        `${id}.${file_format}`,
        this.imageVersion,
      ),
      size,
    );
  }

  public getAACardIdData(aaCardId: string): T | undefined {
    if (!aaCardId) {
      return undefined;
    }
    const aa = this.getAAFromCardId(aaCardId);
    return this.getCardData(aa.card_number);
  }

  // Abstract methods that must be implemented by subclasses
  protected abstract clean(data: Record<string, any>): Record<string, T>;
  public abstract cleanCardData(): Record<string, T>;
  public abstract getCardData(cardId: string): T | undefined;
}
